import { Injectable } from '@angular/core';
import {
  CreateCreditNoteRequest,
  CreditNoteApiService,
  CreditNoteType,
  GetCreditNoteRequest,
  GetMultiCreditNoteRequest,
  HostService,
  ListCreditNotesRequest,
  PagedRequestOptions,
  SendCreditNoteRequest,
} from '@vendasta/billing';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreditNote, creditNoteFromApi, CreditNoteLineItem, creditNoteToApi, CreditNoteAppliedTo } from './credit-note';
import { FetchCreditNotesFilter } from './list-credit-note';
import { PagedResponse } from '../core';
import { Currency } from '../pricing';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class CreditNoteService {
  constructor(
    private readonly creditNoteApi: CreditNoteApiService,
    private http: HttpClient,
    private hostService: HostService,
  ) {}

  getMulti(creditNoteIds: string[]): Observable<CreditNote[]> {
    const req = new GetMultiCreditNoteRequest({ ids: creditNoteIds });
    return this.creditNoteApi.getMulti(req).pipe(
      map((resp) => {
        return resp.creditNotes.map((creditNote) => creditNoteFromApi(creditNote));
      }),
    );
  }

  list(filters: FetchCreditNotesFilter, cursor: string, pageSize: number): Observable<PagedResponse<CreditNote>> {
    const req = new ListCreditNotesRequest({
      filters: filters,
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
    });
    return this.creditNoteApi.list(req).pipe(
      map((response) => {
        const items = (response.creditNotes || []).map((creditNote) => creditNoteFromApi(creditNote));
        return new PagedResponse(items, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  create(creditNote: CreditNote, creditNoteType: CreditNoteType): Observable<string> {
    const creditNoteReq = creditNoteToApi(creditNote);
    creditNoteReq.appliedTo?.map((appliedTo) => {
      appliedTo.creditNoteType = creditNoteType;
    });
    const req = new CreateCreditNoteRequest({ creditNote: creditNoteReq });
    return this.creditNoteApi.create(req).pipe(map((resp) => resp.id));
  }

  get(creditNoteId: string): Observable<CreditNote> {
    const req = new GetCreditNoteRequest({ id: creditNoteId });
    return this.creditNoteApi.get(req).pipe(map((resp) => creditNoteFromApi(resp.creditNote)));
  }

  calculate(
    merchantId: string,
    customerId: string,
    id: string,
    currency: Currency,
    items: CreditNoteLineItem[],
    appliedTo: CreditNoteAppliedTo[],
  ): Observable<CreditNote> {
    const req = new CreateCreditNoteRequest({
      creditNote: creditNoteToApi({
        merchantId: merchantId,
        customerId: customerId,
        id: id,
        currency: currency,
        items: items,
        appliedTo: appliedTo,
      } as CreditNote),
    });
    return this.creditNoteApi.calculate(req).pipe(map((resp) => creditNoteFromApi(resp.creditNote)));
  }

  send(creditNoteId: string, emailAddresses: string[]): Observable<unknown> {
    const req = new SendCreditNoteRequest({ id: creditNoteId, emailAddresses });
    return this.creditNoteApi.send(req);
  }

  preview(creditNoteId: string, merchantId: string, customerId: string): Observable<string> {
    const url = `${this.hostService.hostWithScheme}/credit-note/preview`;
    return this.http.post(
      url,
      {
        credit_note_id: creditNoteId,
        merchant_id: merchantId,
        customer_id: customerId,
      },
      {
        responseType: 'text',
        withCredentials: true,
      },
    );
  }
}
